import React from 'react';
import { useAuthDemo, useAuthUnified } from '@/hooks/shared/useAuthUnified';
import { DemoEmployeeSelector } from '@/domains/admin/components/auth/DemoEmployeeSelector';
import { Shield, X } from 'lucide-react';

/**
 * Demo Role Toggle Component
 * Admin impersonation tool — pick a user to see what they see.
 * Access gated by isSuperAdmin or admin role.
 */
const DemoRoleToggle: React.FC = () => {
  const {
    isDemoMode,
    demoEmployee: selectedEmployee,
    exitDemo,
    isDevEnvironment,
  } = useAuthDemo();

  // Get the actual logged-in user (not the demo user) to check admin permissions
  const { userProfile, hasGroupRouteAccess } = useAuthUnified();

  // Don't render in production
  if (!isDevEnvironment) {
    return null;
  }

  // Only show to super-admins or users with admin access
  if (!userProfile) return null;
  if (!userProfile.isSuperAdmin && !hasGroupRouteAccess('/admin/users')) {
    return null;
  }

  // Display group memberships for the demo user
  const groupNames = (selectedEmployee?.groups || []).map(
    g => g.charAt(0).toUpperCase() + g.slice(1).replace(/-/g, ' ')
  );

  const handleClearDemo = () => {
    exitDemo();
  };

  return (
    <div className="space-y-3">
      {/* Current Demo User Display */}
      {isDemoMode && selectedEmployee && (
        <div className="flex items-center space-x-2 p-3 rounded-lg border border-purple-200 bg-purple-50 text-purple-600">
          <Shield className="h-5 w-5" />
          <div className="flex-1">
            <div className="font-medium text-sm">
              {selectedEmployee.employee_data?.name || 'Unknown Employee'}
            </div>
            <div className="text-xs opacity-75">
              {selectedEmployee.employee_data?.team_name}
            </div>
            {/* Group membership badges */}
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedEmployee.isSuperAdmin && (
                <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">Super Admin</span>
              )}
              {groupNames.map(name => (
                <span
                  key={name}
                  className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded"
                >
                  {name}
                </span>
              ))}
              {groupNames.length === 0 && !selectedEmployee.isSuperAdmin && (
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded">No groups</span>
              )}
            </div>
          </div>
          <button
            onClick={handleClearDemo}
            className="p-1 text-gray-400 hover:text-gray-600"
            title="Exit demo mode"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Employee Selector */}
      <DemoEmployeeSelector />
    </div>
  );
};

export default DemoRoleToggle;
